import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FullPageRedirect from "../components/FullPageRedirect.jsx";
import { isAppScreenLocked } from "../config/appScreenLockRegistry";
import { useRoleNavigation } from "../../public/hooks/useRoleNavigation";
import { getJwtRoleSlug } from "../../public/utils/jwtRole";
import { getAuthItem } from "../../public/utils/authStorage";
import { isUnrestrictedAdminRole } from "../../public/utils/roleAccess";
import {
  clearSessionStorage,
  isJwtExpired,
} from "../../public/api/httpClient";
import { getRoleHomePath } from "../../public/utils/authSession";

function normalizePath(p) {
  const s = String(p ?? "").trim();
  if (!s) return "";
  return s.length > 1 && s.endsWith("/") ? s.slice(0, -1) : s;
}

function flattenNavItems(items, out = []) {
  for (const item of items || []) {
    if (item?.to) out.push(item);
    if (Array.isArray(item?.children)) {
      flattenNavItems(item.children, out);
    }
  }
  return out;
}

/** Unified cheque screen — menu may still list legacy /app/cheques paths until nav sync. */
const CHEQUE_ISSUES_NAV_PATHS = new Set([
  "/app/cheques/issues",
  "/app/cheques",
  "/app/cheques/control",
]);

function navIncludesPath(navItems, pathname, screenKey = null) {
  const target = normalizePath(pathname);
  const flat = flattenNavItems(navItems);
  if (screenKey === "cheque-issues") {
    return flat.some((item) =>
      CHEQUE_ISSUES_NAV_PATHS.has(normalizePath(item.to))
    );
  }
  return flat.some((item) => normalizePath(item.to) === target);
}

/** Inbox-style screens: any signed-in user may open the URL (menu link optional). */
const PERSONAL_INBOX_SCREEN_KEYS = new Set(["notifications", "settings"]);

/** Donor portal screens: JWT role must be donor (not menu permission alone). */
const DONOR_ONLY_SCREEN_KEYS = new Set([
  "donation-history",
  "donor-donation-tracking",
]);

/**
 * Wraps an /app screen. Unlocked screenKeys: always render children.
 * Locked screenKeys: require JWT + route present on role navigation menu.
 */
export default function AppScreenAccessGuard({ screenKey, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locked = isAppScreenLocked(screenKey);

  const roleSlug = useMemo(() => {
    if (typeof window === "undefined") return "";
    return getJwtRoleSlug(getAuthItem("jwt_token")) || "";
  }, [location.pathname, locked]);

  const hasToken = useMemo(() => {
    if (typeof window === "undefined") return false;
    return Boolean(getAuthItem("jwt_token"));
  }, [location.pathname, locked]);

  const { navItems, loading, error } = useRoleNavigation(
    roleSlug,
    locked && hasToken && Boolean(roleSlug),
  );

  if (!locked) {
    return children;
  }

  const token =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("jwt_token")
      : null;

  const returnPath = `${location.pathname}${location.search}`;

  if (token && isJwtExpired(token)) {
    clearSessionStorage();
    return <FullPageRedirect reason="session_expired" from={returnPath} />;
  }

  if (
    hasToken &&
    DONOR_ONLY_SCREEN_KEYS.has(screenKey) &&
    roleSlug !== "donor"
  ) {
    return (
      <div className="app-screen-guard app-screen-guard--blocked">
        <h2 className="app-screen-guard__title">Donor login required</h2>
        <p className="app-screen-guard__text">
          You must login as donor to experience the page.
        </p>
        <div className="app-screen-guard__actions">
          <Link to="/donor-login" className="app-screen-guard__link">
            Go to donor login
          </Link>
          {roleSlug ? (
            <Link
              to={getRoleHomePath(roleSlug)}
              className="app-screen-guard__link app-screen-guard__link--muted"
            >
              Back to home
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  if (hasToken && isUnrestrictedAdminRole(roleSlug)) {
    return children;
  }

  if (hasToken && PERSONAL_INBOX_SCREEN_KEYS.has(screenKey)) {
    return children;
  }

  if (!hasToken) {
    return <FullPageRedirect reason="auth_required" from={returnPath} />;
  }

  if (!roleSlug) {
    return (
      <div className="app-screen-guard app-screen-guard--blocked">
        <h2 className="app-screen-guard__title">Role not found</h2>
        <p className="app-screen-guard__text">
          Your session does not include a role. Sign in again with a staff account.
        </p>
        <button
          type="button"
          className="app-screen-guard__btn"
          onClick={() => {
            localStorage.removeItem("jwt_token");
            localStorage.removeItem("csrf_token");
            navigate("/user-login", { replace: true });
          }}
        >
          Go to login
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="app-screen-guard app-screen-guard--loading">
        <p>Checking access…</p>
      </div>
    );
  }

  const allowed = navIncludesPath(navItems, location.pathname, screenKey);

  if (error || !allowed) {
    return (
      <div className="app-screen-guard app-screen-guard--blocked">
        <h2 className="app-screen-guard__title">No access to this screen</h2>
        <p className="app-screen-guard__text">
          The URL for <strong>{screenKey}</strong> is locked. Your role does not have this
          item in the menu. Assign submodule permissions in User builder, save, then use
          the sidebar link.
        </p>
        {error ? (
          <p className="app-screen-guard__error" role="alert">
            {error}
          </p>
        ) : null}
        <Link to={getRoleHomePath(roleSlug)} className="app-screen-guard__link">
          Back to home
        </Link>
      </div>
    );
  }

  return children;
}
